import {
  ShieldCheck, Church, GraduationCap, ClipboardCheck, CalendarCheck,
  FileSearch, Siren, Check, ArrowRight, Cctv, Fingerprint, Cable, Network, BellRing,
} from "lucide-react";
import { PageHero, SectionHeading, Reveal, CTABand, GoldButton, GhostButton, usePageMeta } from "@/components/kit";
import { HERO_BG, SLOGAN, PHONE, PHONE_TEL } from "@/lib/site";

const PLANS = [
  {
    id: "sanctuary",
    icon: Church,
    name: "Sanctuary Plan",
    audience: "For Churches",
    cadence: "Quarterly Visits",
    intro: "Sunday never moves. Your systems shouldn't either.",
    featured: true,
    features: [
      "Camera lenses cleaned, focused, and verified each quarter",
      "Recorder health, storage capacity, and remote-viewing checks",
      "Access control batteries, readers, and door hardware tested",
      "Video intercom video, audio, and door-release verification",
      "Pre-event system check before Easter and Christmas",
      "Preferred church rate with priority weekend emergency dispatch",
    ],
  },
  {
    id: "campus",
    icon: GraduationCap,
    name: "Campus Plan",
    audience: "For Schools",
    cadence: "Quarterly + Summer Deep Service",
    intro: "Entry control that works every morning, not just the first one.",
    features: [
      "Door hardware, strikes, and mag locks tested door by door",
      "Credential audit — former staff access reviewed and revoked",
      "Intercom and front-office release tested before each term",
      "Camera coverage walk — blind spots flagged and corrected",
      "Summer deep service while hallways are empty",
      "Preferred school rate with documented reports for administrators",
    ],
  },
  {
    id: "guardian",
    icon: ShieldCheck,
    name: "Guardian Plan",
    audience: "For Any Facility",
    cadence: "Bi-Annual Visits",
    intro: "Steady oversight for businesses and first-responder facilities.",
    features: [
      "Full system health check across all low-voltage systems",
      "Fiber and Ethernet links tested and re-certified",
      "Firmware updates and security patches applied",
      "Network closet inspection, dressing touch-up, and labeling",
      "Battery backup and power supply testing",
      "Preferred facility rate with after-hours emergency priority",
    ],
  },
];

const CHECKLIST = [
  { icon: Cctv, system: "Security Cameras", items: ["Lens cleaning & focus", "Field-of-view verification", "Night-vision test", "Recorder & storage health", "Remote viewing check"] },
  { icon: Fingerprint, system: "Access Control", items: ["Reader & keypad test", "Strike & mag lock operation", "Battery backup test", "Credential audit", "Door alignment & latching"] },
  { icon: Cable, system: "Fiber Optics", items: ["Link power-level test", "Connector inspection & cleaning", "Enclosure & tray check", "Documentation update", "Spare capacity review"] },
  { icon: Network, system: "Ethernet & Data", items: ["Drop testing & toning", "PoE output verification", "Closet dressing touch-up", "Label & map update", "Patch panel inspection"] },
  { icon: BellRing, system: "Video Intercom", items: ["Video & audio test", "Door-release verification", "Mobile calling check", "Station cleaning", "Call-routing review"] },
];

const STEPS = [
  { icon: FileSearch, t: "Free System Survey", d: "We walk your facility, inventory every system, and document its current condition — no cost, no obligation." },
  { icon: ClipboardCheck, t: "Custom Plan & Quote", d: "You get a plan matched to your systems and calendar, quoted per facility with your church or school rate applied." },
  { icon: CalendarCheck, t: "Scheduled Visits", d: "We show up on schedule — before your busiest seasons, never during them — and service every system on the checklist." },
  { icon: Siren, t: "Priority When It Matters", d: "Plan members move to the front of the line for after-hours and weekend emergencies, at preferred rates." },
];

export default function MaintenancePlansPage() {
  usePageMeta(
    "Maintenance Plans for Churches & Schools | Redeemer Solutions LLC",
    "Scheduled maintenance plans for church and school security cameras, access control, fiber, Ethernet, and intercom systems across Middle Tennessee. Custom-quoted per facility."
  );
  return (
    <div data-testid="maintenance-plans-page">
      <PageHero
        testid="maintenance-hero"
        eyebrow="Maintenance Plans"
        lines={["An Ounce of Prevention,", { t: "a Pound of Protection.", gold: true }]}
        sub="Scheduled care for the systems that protect your people — so a failure never finds out before you do. Built for churches and schools, priced for their budgets."
        image={HERO_BG}
        imageAlt="Scheduled maintenance for security and low-voltage systems"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <SectionHeading
          eyebrow="Choose Your Plan"
          title="Stewardship, on a schedule."
          sub="Every plan is custom-quoted per facility after a free system survey — no one-size-fits-all pricing, and special rates always apply for churches, schools, and emergency personnel facilities."
          testid="plans-heading"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-[#121216] rounded-sm p-8 gold-glow-card h-full flex flex-col border ${
                  p.featured ? "border-gold/50 shadow-[0_0_36px_rgba(212,175,55,0.12)]" : "border-gold/15"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3.5 left-8 font-mono-label text-[10px] uppercase tracking-[0.2em] bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold px-3.5 py-1.5 rounded-sm">
                    Most Requested
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-gold/40 bg-gold/5">
                    <p.icon size={22} className="text-gold" />
                  </div>
                  <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-gold-champagne/80 border border-gold/25 rounded-full px-3 py-1">
                    {p.cadence}
                  </span>
                </div>
                <p className="font-mono-label text-[11px] uppercase tracking-[0.24em] text-gold mt-7">{p.audience}</p>
                <h3 className="font-display text-2xl font-bold text-[#F4EFE6] mt-2">{p.name}</h3>
                <p className="text-zinc-400 text-sm italic mt-2">{p.intro}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-gold/10">
                  <GoldButton to="/request-quote" testid={`plan-quote-button-${p.id}`} className="w-full">
                    Request This Plan <ArrowRight size={15} />
                  </GoldButton>
                  <p className="text-center text-xs text-zinc-500 mt-3">Custom-quoted after a free survey</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="checklist-section" className="border-y border-gold/15 bg-[#0D0D10] relative overflow-hidden">
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <SectionHeading
            eyebrow="Every Visit"
            title="What we check, every single time."
            sub="One visit covers your whole low-voltage ecosystem. You receive a written condition report after each service."
            align="center"
            testid="checklist-heading"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
            {CHECKLIST.map((c, i) => (
              <Reveal key={c.system} delay={i * 0.07}>
                <div data-testid={`checklist-${c.system.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-[#121216] border border-gold/15 rounded-sm p-6 gold-glow-card h-full">
                  <c.icon size={20} className="text-gold" />
                  <h3 className="font-display text-sm font-semibold text-[#F4EFE6] mt-4 leading-snug">{c.system}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {c.items.map((item) => (
                      <li key={item} className="flex gap-2 text-xs text-zinc-400 leading-relaxed">
                        <Check size={12} className="text-gold mt-0.5 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <SectionHeading
          eyebrow="How It Works"
          title="From first survey to standing guard."
          testid="steps-heading"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div data-testid={`step-${i + 1}`} className="relative bg-[#121216] border border-gold/15 rounded-sm p-7 gold-glow-card h-full">
                <span className="font-display text-5xl font-bold text-gold/15 absolute top-5 right-6">{String(i + 1).padStart(2, "0")}</span>
                <s.icon size={22} className="text-gold" />
                <h3 className="font-display text-lg font-semibold text-[#F4EFE6] mt-4">{s.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 border border-gold/20 bg-[#0D0D10] rounded-sm px-8 py-7 text-center">
            <p className="text-zinc-300 text-sm">
              Prefer to talk it through? Call <a href={PHONE_TEL} data-testid="plans-call-link" className="text-gold hover:text-gold-champagne transition-colors">{PHONE}</a> and
              ask about maintenance for your facility.
            </p>
            <GhostButton href={PHONE_TEL} testid="plans-call-button" className="shrink-0">Call Now</GhostButton>
          </div>
        </Reveal>
      </section>

      <CTABand title={SLOGAN} sub="Start with a free system survey — we'll document what you have and quote a plan that fits your facility and budget." />
    </div>
  );
}
